import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
interface AuthUser {
  id: string;
  email: string;
  name?: string;
}
interface AuthContextType {
  adminUser: AuthUser | null;
  isAdminLoading: boolean;
  adminLogin: (email: string, password: string) => Promise<{
    error: string | null;
  }>;
  adminLogout: () => Promise<void>;
  customerUser: AuthUser | null;
  isCustomerLoading: boolean;
  customerSignup: (name: string, email: string, password: string, type: 'customer' | 'business', company?: string, phone?: string, address?: string, state?: string, zip?: string) => Promise<{
    error: string | null;
  }>;
  customerLogin: (email: string, password: string) => Promise<{
    error: string | null;
    type?: 'customer' | 'business';
  }>;
  customerLogout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [adminUser, setAdminUser] = useState<AuthUser | null>(null);
  const [isAdminLoading, setIsAdminLoading] = useState(true);
  const [customerUser, setCustomerUser] = useState<AuthUser | null>(null);
  const [isCustomerLoading, setIsCustomerLoading] = useState(true);
  useEffect(() => {
    // Check for existing session on mount
    supabase.auth.getSession().then(async ({
      data: {
        session
      }
    }) => {
      if (session?.user) {
        // Check if user is an admin or customer
        const {
          data: customerData
        } = await supabase.from('customers').select('name, type').eq('id', session.user.id).maybeSingle();
        if (customerData) {
          setCustomerUser({
            id: session.user.id,
            email: session.user.email || '',
            name: customerData.name
          });
        } else {
          // Assume admin if not in customers table
          setAdminUser({
            id: session.user.id,
            email: session.user.email || ''
          });
        }
      }
      setIsAdminLoading(false);
      setIsCustomerLoading(false);
    });
    // Listen for auth state changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const {
          data: customerData
        } = await supabase.from('customers').select('name, type').eq('id', session.user.id).maybeSingle();
        if (customerData) {
          setCustomerUser({
            id: session.user.id,
            email: session.user.email || '',
            name: customerData.name
          });
          setAdminUser(null);
        } else {
          setAdminUser({
            id: session.user.id,
            email: session.user.email || ''
          });
          setCustomerUser(null);
        }
      } else {
        setAdminUser(null);
        setCustomerUser(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  const adminLogin = async (email: string, password: string): Promise<{
    error: string | null;
  }> => {
    const {
      data,
      error
    } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) {
      return {
        error: error.message
      };
    }
    if (data.user) {
      setAdminUser({
        id: data.user.id,
        email: data.user.email || ''
      });
    }
    return {
      error: null
    };
  };
  const adminLogout = async () => {
    await supabase.auth.signOut();
    setAdminUser(null);
  };
  const customerSignup = async (name: string, email: string, password: string, type: 'customer' | 'business', company?: string, phone?: string, address?: string, state?: string, zip?: string): Promise<{
    error: string | null;
  }> => {
    const {
      data,
      error
    } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          type
        }
      }
    });
    if (error) return {
      error: error.message
    };
    if (data.user) {
      // Insert into customers table
      const {
        error: dbError
      } = await supabase.from('customers').insert({
        id: data.user.id,
        name,
        email,
        phone: phone || '',
        company: company || null,
        address: address || null,
        state: state || null,
        zip: zip || null,
        type,
        status: 'active',
        services: [],
        total_spent: 0,
        created_at: new Date().toISOString(),
        last_login: new Date().toISOString()
      });
      if (dbError) {
        console.error('Error creating customer record:', dbError);
        return {
          error: 'Account created but failed to initialize profile.'
        };
      }
      setCustomerUser({
        id: data.user.id,
        email: data.user.email || '',
        name
      });
    }
    return {
      error: null
    };
  };
  const customerLogin = async (email: string, password: string): Promise<{
    error: string | null;
    type?: 'customer' | 'business';
  }> => {
    const {
      data,
      error
    } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) return {
      error: error.message
    };
    if (data.user) {
      // Fetch customer record to get type and name
      const {
        data: customerData,
        error: fetchError
      } = await supabase.from('customers').select('name, type').eq('id', data.user.id).maybeSingle();
      if (fetchError || !customerData) {
        await supabase.auth.signOut();
        return {
          error: 'Invalid customer account.'
        };
      }
      // Update last login
      await supabase.from('customers').update({
        last_login: new Date().toISOString()
      }).eq('id', data.user.id);
      setCustomerUser({
        id: data.user.id,
        email: data.user.email || '',
        name: customerData.name
      });
      return {
        error: null,
        type: customerData.type
      };
    }
    return {
      error: 'Unknown error occurred'
    };
  };
  const customerLogout = async () => {
    await supabase.auth.signOut();
    setCustomerUser(null);
  };
  return <AuthContext.Provider value={{
    adminUser,
    isAdminLoading,
    adminLogin,
    adminLogout,
    customerUser,
    isCustomerLoading,
    customerSignup,
    customerLogin,
    customerLogout
  }} data-id="element-1994">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}